class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector("details");
    this.summaryToggle = this.querySelector("summary");

    this.detailsContainer.addEventListener(
      "keyup",
      (event) => event.code.toUpperCase() === "ESCAPE" && this.close()
    );
    this.summaryToggle.addEventListener("click", this.onSummaryClick.bind(this));

    const closeBtn = this.querySelector('button[type="button"]');
    if (closeBtn) closeBtn.addEventListener("click", this.close.bind(this));


    // Predictive search elements (only exist when predictive is enabled)
    this.inputEl = this.querySelector(".predictive-search-field");
    this.predictiveContainer = this.querySelector("[data-predictive-search]");
    this.comboboxEl = this.querySelector('.field[role="combobox"]');

    // Ensure predictive container has an id for aria-controls
    if (this.predictiveContainer && !this.predictiveContainer.id) {
      this.predictiveContainer.id = "Predictive-Search-Results-Modal";
    }

    // Setup combobox relationships
    if (this.comboboxEl && this.predictiveContainer?.id) {
      this.comboboxEl.setAttribute("aria-controls", this.predictiveContainer.id);
      this.comboboxEl.setAttribute("aria-owns", this.predictiveContainer.id);
      this.comboboxEl.setAttribute("aria-expanded", "false");
    }

    // Remove aria-expanded from input if any script adds it
    // and keep it removed forever while modal exists.
    this._stripInputExpanded = this._stripInputExpanded.bind(this);
  }

  isOpen() {
    return this.detailsContainer.hasAttribute("open");
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest("details").hasAttribute("open") ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains("modal-overlay")) {
      this.close(false);
    }
  }

  // --- A11Y helpers ---
  _stripInputExpanded() {
    if (!this.inputEl) return;
    if (this.inputEl.hasAttribute("aria-expanded")) {
      this.inputEl.removeAttribute("aria-expanded");
    }
  }

  _isPredictiveActuallyOpen() {
    if (!this.predictiveContainer) return false;

    // 1) If theme uses [hidden] or aria-hidden to close:
    if (this.predictiveContainer.hasAttribute("hidden")) return false;
    if (this.predictiveContainer.getAttribute("aria-hidden") === "true") return false;

    // 2) If theme collapses via CSS (display/visibility) when closed:
    const cs = window.getComputedStyle(this.predictiveContainer);
    if (cs.display === "none" || cs.visibility === "hidden") return false;

    // 3) If theme uses an "open" class:
    const openByClass =
      this.predictiveContainer.classList.contains("is-open") ||
      this.predictiveContainer.classList.contains("active") ||
      this.predictiveContainer.classList.contains("predictive-search--open");

    // 4) If results exist:
    const hasResults =
      !!this.predictiveContainer.querySelector(
        ".predictive-search__results, .predictive-search__result-group, [role='listbox'], [role='option'], li, a, button"
      ) && (this.predictiveContainer.textContent || "").trim().length > 0;

    // Many themes "close" by emptying results. So hasResults is the most reliable signal.
    return Boolean(openByClass || hasResults);
  }

  _syncExpandedState() {
    if (!this.comboboxEl) return;

    const expanded = this._isPredictiveActuallyOpen();
    this.comboboxEl.setAttribute("aria-expanded", expanded ? "true" : "false");

    // Always ensure input never holds aria-expanded
    this._stripInputExpanded();
  }

  _startPredictiveObservers() {
    // No predictive search enabled
    if (!this.predictiveContainer || !this.comboboxEl) return;

    // 1) Watch predictive container for class/visibility changes and result injection/removal
    this._predictiveObserver?.disconnect();
    this._predictiveObserver = new MutationObserver(() => this._syncExpandedState());
    this._predictiveObserver.observe(this.predictiveContainer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "hidden", "style", "aria-hidden"],
    });

    // 2) Watch input for aria-expanded being added by theme scripts
    if (this.inputEl) {
      this._inputObserver?.disconnect();
      this._inputObserver = new MutationObserver(() => this._stripInputExpanded());
      this._inputObserver.observe(this.inputEl, {
        attributes: true,
        attributeFilter: ["aria-expanded"],
      });

      // Also resync on typing/focus (some themes toggle results on input events)
      this._onInputEvent = () => this._syncExpandedState();
      this.inputEl.addEventListener("input", this._onInputEvent);
      this.inputEl.addEventListener("focus", this._onInputEvent);
      this.inputEl.addEventListener("blur", this._onInputEvent);
    }

    // Initial sync
    this._syncExpandedState();
  }

  _stopPredictiveObservers() {
    this._predictiveObserver?.disconnect();
    this._predictiveObserver = null;

    this._inputObserver?.disconnect();
    this._inputObserver = null;

    if (this.inputEl && this._onInputEvent) {
      this.inputEl.removeEventListener("input", this._onInputEvent);
      this.inputEl.removeEventListener("focus", this._onInputEvent);
      this.inputEl.removeEventListener("blur", this._onInputEvent);
    }
    this._onInputEvent = null;

    // On close, always collapse
    if (this.comboboxEl) this.comboboxEl.setAttribute("aria-expanded", "false");
    this._stripInputExpanded();
  }

  // --- Existing focus trap logic ---
  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest("details").setAttribute("open", true);
    document.body.addEventListener("click", this.onBodyClickEvent);
    document.body.classList.add("overflow-hidden", "show--new--header--search");

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );

    // Start predictive a11y syncing once modal opens (DOM is stable)
    this._startPredictiveObservers();

    setTimeout(() => {
      this.focusElement = this.detailsContainer.querySelector(".predictive-search-field");
      if (this.focusElement) this.focusElement.focus();
      // One more sync after focus since some themes open results on focus
      this._syncExpandedState();
    }, 200);
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute("open");
    document.body.removeEventListener("click", this.onBodyClickEvent);
    document.body.classList.remove("overflow-hidden", "show--new--header--search");

    this._stopPredictiveObservers();
  }
}

customElements.define("details-modal", DetailsModal);
